import React, { useState, useEffect } from 'react';
import { TriageData, TriageExplanation, ExplanationFactor, TriageAIFeedback, TriageOutcome, SymptomWithSource } from '@/types/triage';
import { explainTriage } from '@/utils/manchesterTriageRules';
import { triageAuditService } from '@/services/triageAuditService';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle, Info, HelpCircle, Loader2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

// Helper functions
const getUrgencyColor = (urgency: string): string => {
  switch (urgency?.toLowerCase()) {
    case 'immediate': return 'bg-red-500 text-white';
    case 'urgent': return 'bg-orange-500 text-white';
    case 'standard': return 'bg-yellow-500 text-black';
    case 'non-urgent': return 'bg-green-500 text-white';
    default: return 'bg-blue-500 text-white';
  }
};

const getUrgencyLabel = (urgency: string): string => {
  switch (urgency?.toLowerCase()) {
    case 'immediate': return 'Immediate (Red)';
    case 'urgent': return 'Urgent (Orange)';
    case 'standard': return 'Standard (Yellow)';
    case 'non-urgent': return 'Non-Urgent (Green)';
    default: return 'Unknown (Blue)';
  }
};

const getConfidenceLabel = (confidence: number): string => {
  if (confidence >= 0.9) return 'Very High';
  if (confidence >= 0.75) return 'High';
  if (confidence >= 0.6) return 'Moderate';
  if (confidence >= 0.4) return 'Low';
  return 'Very Low';
};

const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.75) return 'bg-green-100 text-green-800 border-green-300';
  if (confidence >= 0.6) return 'bg-blue-100 text-blue-800 border-blue-300';
  if (confidence >= 0.4) return 'bg-orange-100 text-orange-800 border-orange-300';
  return 'bg-red-100 text-red-800 border-red-300';
};

interface TriageExplanationPanelProps {
  triageData: TriageData;
  patientId?: string;
  triageId?: string;
  triageOutcome?: TriageOutcome;
  symptomsWithSources?: SymptomWithSource[];
  processedSymptoms?: string[]; // Added prop for processed symptoms
  isOverridden?: boolean;
  onFeedbackSubmit?: (feedback: TriageAIFeedback) => void;
  aiConfidence?: number; // Optional prop to ensure consistency with overall AI assessment
  overrideUrgency?: string; // Optional prop to override the urgency level from the explanation
  panelId?: string; // Unique identifier for the panel instance for better debugging
}

export const TriageExplanationPanel: React.FC<TriageExplanationPanelProps> = ({
  triageData,
  patientId,
  triageId,
  panelId = 'default',
  triageOutcome,
  symptomsWithSources: propSymptomWithSources,
  processedSymptoms, // Added prop for processed symptoms
  isOverridden = false,
  onFeedbackSubmit,
  aiConfidence, // Prop to ensure consistency with overall AI assessment
  overrideUrgency // Prop to override the urgency level from the explanation
}) => {
  // Enhanced debug logging for all component data
  React.useEffect(() => {
    console.log('[TriageExplanationPanel] Initialized with data:', {
      triageId,
      patientId,
      symptoms: triageData?.symptoms,
      symptomsWithSources: propSymptomWithSources,
      processedSymptoms,
      triageOutcome,
      isOverridden,
      aiConfidence,
      overrideUrgency
    });
    return () => {
      console.log('[TriageExplanationPanel] Component cleanup or props changing');
    };
  }, [triageData, propSymptomWithSources, processedSymptoms]);

  const [explanation, setExplanation] = useState<TriageExplanation | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbackRating, setFeedbackRating] = useState<number | null>(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean>(false);
  const [feedbackText, setFeedbackText] = useState<string>('');

  // Add a new state for tracking retry attempts
  const [auditLogRetryCount, setAuditLogRetryCount] = useState(0);
  const MAX_RETRY_ATTEMPTS = 3;

  // Determine the effective triageId and patientId
  // Prioritize direct props, then fall back to deriving from other props
  // Log the determination process for debugging
  const effectiveTriageId = triageId; // TriageOutcome does not have 'id'. The triageId prop is the source.
  const effectivePatientId = patientId || triageData?.patientId;
  
  // Add a warning if patientId is missing
  React.useEffect(() => {
    if (!effectivePatientId) {
      console.warn('[TriageExplanationPanel] Warning: No patient ID available for audit logging');
    }
  }, [effectivePatientId]);

  // Log the determined IDs
  React.useEffect(() => {
    console.log('[TriageExplanationPanel] Effective IDs determined:', {
      effectiveTriageId,
      effectivePatientId,
      source: {
        triageIdFromProps: triageId ? true : false,
        patientIdFromProps: patientId ? true : false,
        patientIdFromTriageData: triageData?.patientId ? true : false
      }
    });
  }, [effectiveTriageId, effectivePatientId, triageId, patientId, triageData]);

  // Debug log to verify ID values
  React.useEffect(() => {
    console.log('TriageExplanationPanel - IDs for audit logging:', { 
      effectiveTriageId, 
      effectivePatientId,
      originalTriageId: triageId,
      originalPatientId: patientId
    });
  }, [effectiveTriageId, effectivePatientId, triageId, patientId]);

  // Add a useEffect to handle delayed audit logging when IDs become available
  useEffect(() => {
    // Only attempt retries if we have an explanation but are missing IDs
    if (explanation && (!effectiveTriageId || !effectivePatientId) && auditLogRetryCount < MAX_RETRY_ATTEMPTS) {
      console.log(`[TriageExplanationPanel] Scheduling retry #${auditLogRetryCount + 1} for audit logging in ${(auditLogRetryCount + 1) * 1000}ms`, {
        panelId
      });
      
      // Exponential backoff for retries
      const retryTimeout = setTimeout(() => {
        console.log('[TriageExplanationPanel] Retrying audit log...', {
          retryCount: auditLogRetryCount + 1,
          hasTriageId: !!effectiveTriageId,
          hasPatientId: !!effectivePatientId,
          panelId
        });
        
        setAuditLogRetryCount(prev => prev + 1);
        
        // If IDs are now available, attempt to log
        if (effectiveTriageId && effectivePatientId) {
          logExplanationToAuditService(explanation);
        }
      }, (auditLogRetryCount + 1) * 1000); // Increasing delay with each retry
      
      return () => clearTimeout(retryTimeout);
    }
  }, [explanation, effectiveTriageId, effectivePatientId, auditLogRetryCount, panelId]);

  // Extract the audit logging logic into a separate function for reuse
  const logExplanationToAuditService = (explanationToLog: TriageExplanation) => {
    if (!effectiveTriageId || !effectivePatientId) {
      console.warn('[TriageExplanationPanel] Cannot log explanation view - missing IDs', {
        hasTriageId: !!effectiveTriageId,
        hasPatientId: !!effectivePatientId,
        panelId
      });
      return;
    }
    
    console.log('[TriageExplanationPanel] Logging explanation viewed event to audit service', {
      triageId: effectiveTriageId,
      patientId: effectivePatientId,
      timestamp: new Date().toISOString(),
      panelId
    });
    
    triageAuditService.logExplanationViewed({
      triageId: effectiveTriageId,
      patientId: effectivePatientId,
      explanation: explanationToLog,
      timestamp: new Date()
    }).catch(err => {
      console.error('[TriageExplanationPanel] Error logging explanation view:', err);
      console.error('[TriageExplanationPanel] Error context:', {
        triageId: effectiveTriageId,
        patientIdExists: !!effectivePatientId,
        explanationExists: !!explanationToLog,
        panelId
      });
    });
  };

  // Generate explanation using the Manchester triage rules
  const generateExplanation = async () => {
    console.log('[TriageExplanationPanel] Generating explanation with data:', {
      symptoms: triageData?.symptoms,
      processedSymptoms,
      symptomsWithSources: propSymptomWithSources,
      age: triageData?.age,
      gender: triageData?.gender,
      medicalHistory: triageData?.medicalHistory,
      isPregnant: triageData?.isPregnant
    });
    try {
      setLoading(true);
      setError(null);

      // Log the detailed explanation for debugging and audit
      console.log('[TriageExplanationPanel] Generated explanation:', {
        urgency: explanation?.urgency,
        confidence: explanation?.confidence,
        factors: explanation?.factors.map(factor => ({
          parameter: factor.parameter,
          value: factor.value,
          impact: factor.impact,
          description: factor.description
        })),
        presentingComplaint: explanation?.presentingComplaint,
        discriminator: explanation?.discriminator,
        processedSymptoms: explanation?.processedSymptoms,
        symptomsWithSources: explanation?.symptomsWithSources,
        rules: explanation?.rules
      });

      // Generate explanation from the triage rules engine
      const explanationResult = await explainTriage(triageData);
      
      // Log the complete explanation for audit and debugging
      console.log('[TriageExplanationPanel] Complete triage explanation generated:', JSON.stringify(explanationResult));
      
      // Store the complete explanation in state
      setExplanation(explanationResult);

      // Reset retry count when generating a new explanation
      setAuditLogRetryCount(0);

      // Log the explanation viewing event only after we have the explanation
      if (explanationResult) {
        logExplanationToAuditService(explanationResult);
      } else {
        console.warn('[TriageExplanationPanel] Skipping audit logging due to missing explanation');
      }

    } catch (err) {
      console.error('Error generating triage explanation:', err);
      setError('Unable to generate triage explanation. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Submit feedback on the explanation
  const submitFeedback = async () => {
    console.log('[TriageExplanationPanel] Submitting feedback:', {
      rating: feedbackRating,
      text: feedbackText,
      triageId: effectiveTriageId,
      patientId: effectivePatientId
    });
    if (feedbackRating === null) return;

    try {
      const feedbackType = 
        feedbackRating >= 4 ? 'helpful' :
        feedbackRating >= 2 ? 'incomplete' :
        'inaccurate';

      const feedback: TriageAIFeedback = {
        triageId,
        patientId,
        feedbackType,
        rating: feedbackRating,
        comments: feedbackText,
        timestamp: new Date()
      };

      // Log the feedback submission
      await triageAuditService.logAIFeedback(feedback);

      // Call the feedback handler if provided
      if (onFeedbackSubmit) {
        console.log('[TriageExplanationPanel] Calling onFeedbackSubmit handler with feedback', {
          feedbackType: feedback.feedbackType,
          rating: feedback.rating,
          hasComments: !!feedback.comments
        });
        onFeedbackSubmit(feedback);
      } else {
        console.log('[TriageExplanationPanel] No feedback handler provided, feedback will only be logged');
      }

      setFeedbackSubmitted(true);

    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Unable to submit feedback. Please try again later.');
    }
  };

  // Load the explanation when the component mounts or when key props change
  useEffect(() => {
    console.log('[TriageExplanationPanel] Triggering explanation generation due to props change/mount');
    generateExplanation();
  }, [triageData, triageId]);

  // Log render state
  React.useEffect(() => {
    console.log('[TriageExplanationPanel] Rendering with state:', {
      hasExplanation: !!explanation,
      loading,
      error,
      feedbackRating,
      feedbackSubmitted
    });
  }, [explanation, loading, error, feedbackRating, feedbackSubmitted]);
  
  return (
    <Card className="border rounded-md shadow-sm my-4">
      <CardHeader className="bg-muted/50">
        <CardTitle className="text-lg flex items-center gap-2">
          <HelpCircle className="h-5 w-5" />
          AI Triage Explanation
          {/* Add data attributes for testing and logging */}
          <span 
            data-testid="triage-explanation-header"
            data-triage-id={effectiveTriageId}
            data-patient-id={effectivePatientId}
            className="sr-only"
          >
            Triage explanation for patient {effectivePatientId} and triage {effectiveTriageId}
          </span>
        </CardTitle>
      </CardHeader>

      <CardContent>
        {loading && (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
            <p className="text-sm text-muted-foreground">
              Generating triage explanation...
            </p>
          </div>
        )}

        {error && (
          <Alert variant="destructive" className="my-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {explanation && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <Badge className={cn("px-3 py-1", getUrgencyColor(overrideUrgency || explanation.urgency))}>
                {getUrgencyLabel(overrideUrgency || explanation.urgency)}
              </Badge>

              <Badge variant="outline" className={cn("px-2 py-1", getConfidenceColor(aiConfidence !== undefined ? aiConfidence : explanation.confidence))}>
                Confidence: {getConfidenceLabel(aiConfidence !== undefined ? aiConfidence : explanation.confidence)}
              </Badge>
            </div>

            <p className="text-sm">
              {isOverridden ? (
                <span className="italic">
                  <strong>Note:</strong> This category has been overridden by a clinician.
                </span>
              ) : (
                <span>
                  Based on the information provided, this triage was categorized as 
                  <strong> {getUrgencyLabel(overrideUrgency || explanation.urgency)}</strong>.
                </span>
              )}
            </p>

            {/* Display presenting complaint and discriminator when available */}
            {explanation.presentingComplaint && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <h4 className="font-semibold mb-1">Clinical Presentation</h4>
                <p className="text-sm">{explanation.presentingComplaint}</p>
                
                {explanation.discriminator && (
                  <div className="mt-2">
                    <h5 className="font-medium text-sm">Key Discriminator</h5>
                    <p className="text-sm">{explanation.discriminator}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Display symptoms with source differentiation */}
            {((explanation?.processedSymptoms && explanation.processedSymptoms.length > 0) || propSymptomWithSources) && (
              <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-md">
                <h4 className="font-semibold mb-2">Symptoms Considered</h4>
                <div className="flex flex-wrap gap-2">
                  {propSymptomWithSources ? (
                    // Use symptoms with sources from props if available
                    propSymptomWithSources.map((symptom, index) => (
                      <Badge 
                        key={index}
                        variant="outline"
                        className={cn(
                          "px-2 py-1", 
                          symptom.source === 'explicit' 
                            ? "bg-green-50 border-green-200 text-green-800" 
                            : "bg-amber-50 border-amber-200 text-amber-800 italic"
                        )}
                      >
                        {symptom.name}
                        {symptom.source === 'extracted' && (
                          <span className="ml-1 text-xs" title="Extracted from clinical notes">*</span>
                        )}
                      </Badge>
                    ))
                  ) : explanation?.symptomsWithSources ? (
                    // If we have source information from explanation, use it to differentiate
                    explanation.symptomsWithSources.map((symptom, index) => (
                      <Badge 
                        key={index}
                        variant="outline"
                        className={cn(
                          "px-2 py-1", 
                          symptom.source === 'explicit' 
                            ? "bg-green-50 border-green-200 text-green-800" 
                            : "bg-amber-50 border-amber-200 text-amber-800 italic"
                        )}
                      >
                        {symptom.name}
                        {symptom.source === 'extracted' && (
                          <span className="ml-1 text-xs" title="Extracted from clinical notes">*</span>
                        )}
                      </Badge>
                    ))
                  ) : explanation?.processedSymptoms ? (
                    // Fallback to just showing the list of symptoms from explanation
                    explanation.processedSymptoms.map((symptom, index) => (
                      <Badge 
                        key={index}
                        variant="outline"
                        className="px-2 py-1 bg-slate-100 border-slate-300"
                      >
                        {symptom}
                      </Badge>
                    ))
                  ) : (
                    // Final fallback to triageData symptoms
                    triageData.symptoms.map((symptom, index) => (
                      <Badge 
                        key={index}
                        variant="outline"
                        className="px-2 py-1 bg-slate-100 border-slate-300"
                      >
                        {symptom}
                      </Badge>
                    ))
                  )}
                </div>
                {(propSymptomWithSources && propSymptomWithSources.some(s => s.source === 'extracted')) || 
                 (explanation?.symptomsWithSources && explanation.symptomsWithSources.some(s => s.source === 'extracted')) ? (
                  <p className="text-xs text-muted-foreground mt-2 italic">
                    * Symptoms extracted from clinical notes
                  </p>
                ) : null}
              </div>
            )}

            <div>
              <h4 className="font-semibold mb-2 mt-4">Key Factors Influencing This Decision:</h4>

              <div className="space-y-3">
                {explanation.factors.map((factor, index) => (
                  <div 
                    key={index} 
                    className={cn(
                      "p-3 border rounded-md flex items-start gap-3",
                      factor.impact === 'high' ? "bg-red-50 border-red-200" : 
                      factor.impact === 'medium' ? "bg-amber-50 border-amber-200" : 
                      "bg-blue-50 border-blue-200"
                    )}
                    onClick={() => {
                      console.log('[TriageExplanationPanel] Factor clicked:', {
                        parameter: factor.parameter,
                        value: factor.value,
                        impact: factor.impact,
                        description: factor.description,
                        triageId: effectiveTriageId,
                        timestamp: new Date().toISOString()
                      });
                    }}
                    data-testid={`triage-factor-${factor.parameter}`}
                    data-impact={factor.impact}
                  >
                    <div className={cn(
                      "mt-0.5",
                      factor.impact === 'high' ? "text-red-600" : 
                      factor.impact === 'medium' ? "text-amber-600" : 
                      "text-blue-600"
                    )}>
                      {factor.impact === 'high' ? (
                        <AlertCircle className="h-5 w-5" />
                      ) : factor.impact === 'medium' ? (
                        <Info className="h-5 w-5" />
                      ) : (
                        <CheckCircle className="h-5 w-5" />
                      )}
                    </div>

                    <div>
                      <p className="font-medium">
                        {factor.parameter}: {factor.value.toString()}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {factor.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {explanation && !feedbackSubmitted ? (
          <div className="mt-6 p-4 bg-muted rounded-md">
            <h4 className="font-semibold mb-2">Was this explanation helpful?</h4>

            <div className="flex items-center gap-4 mb-3">
              <Button 
                variant={feedbackRating === 5 ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  console.log('[TriageExplanationPanel] Helpful feedback selected', {
                    rating: 5,
                    triageId: effectiveTriageId,
                    timestamp: new Date().toISOString()
                  });
                  setFeedbackRating(5);
                }}
                className="gap-2"
                data-testid="feedback-helpful"
              >
                <CheckCircle className="h-4 w-4" />
                Helpful
              </Button>

              <Button 
                variant={feedbackRating === 3 ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  console.log('[TriageExplanationPanel] Incomplete feedback selected', {
                    rating: 3,
                    triageId: effectiveTriageId,
                    timestamp: new Date().toISOString()
                  });
                  setFeedbackRating(3);
                }}
                className="gap-2"
                data-testid="feedback-incomplete"
              >
                <Info className="h-4 w-4" />
                Incomplete
              </Button>

              <Button 
                variant={feedbackRating === 1 ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  console.log('[TriageExplanationPanel] Inaccurate feedback selected', {
                    rating: 1,
                    triageId: effectiveTriageId,
                    timestamp: new Date().toISOString()
                  });
                  setFeedbackRating(1);
                }}
                className="gap-2"
                data-testid="feedback-inaccurate"
              >
                <AlertCircle className="h-4 w-4" />
                Inaccurate
              </Button>
            </div>

            {feedbackRating !== null && (
              <>
                <Textarea
                  placeholder="Additional comments (optional)"
                  value={feedbackText}
                  onChange={(e) => {
                    setFeedbackText(e.target.value);
                    if (e.target.value.length > 0 && e.target.value.length % 10 === 0) {
                      console.log('[TriageExplanationPanel] Feedback text being entered', {
                        textLength: e.target.value.length,
                        triageId: effectiveTriageId,
                        timestamp: new Date().toISOString()
                      });
                    }
                  }}
                  className="min-h-[80px] mb-3"
                  data-testid="feedback-comments"
                />

                <Button
                  onClick={submitFeedback}
                  disabled={feedbackRating === null}
                  data-testid="submit-feedback-button"
                >
                  Submit Feedback
                </Button>
              </>
            )}
          </div>
        ) : explanation && feedbackSubmitted ? (
          <Alert className="mt-4 bg-green-50 border-green-200 text-green-800">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              Thank you for your feedback! It helps improve our AI triage system.
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
      
      <CardFooter className="flex justify-center border-t bg-muted/50 py-2">
        <p className="text-xs text-muted-foreground italic"
          onClick={() => {
            console.log('[TriageExplanationPanel] Footer info clicked', {
              triageId: effectiveTriageId,
              timestamp: new Date().toISOString()
            });
          }}
          data-testid="triage-explanation-footer"
        >
          Glass Box AI Triage System v1.0 - Transparent clinical decision support compliant with EU AI Act
        </p>
      </CardFooter>
    </Card>
  );
};
